import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import OutlinedSelect from '../../../../Components/OutlinedSelect';
import { Grid, Typography, Button, CircularProgress, TextField, Switch } from '@material-ui/core/';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import PropTypes from 'prop-types';
import { AddMoreButton, NumberInput, RemoveButton } from '../AssignLicence';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { IsNumeric, notify } from 'utils';
import { USER_ROLES } from 'utils/constants';
import LearningAccessSection from '../LearningAccessSection';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        height: 180,
        background: '#FFFFFF',
        boxShadow: "0px 4px 30px rgba(12, 41, 84, 0.1)",
        borderRadius: 5
    },
    labelStyle: { 
        marginBottom: '20px', 
        marginTop: '15px', 
        color: '#333333' 
    },
    btn: {
        padding: '8px 22px',
        fontSize: '0.875rem',
        color: 'white',
        borderRadius: '5px',
        marginLeft: 8,
        minWidth: 120,
        [theme.breakpoints.down('xs')]: {
            minWidth: 96,
            padding: '8px 14px',
            marginLeft: 0,
        },
    },
    cancelBtn: {
        padding: '8px 22px',
        background: '#B7B7B7',
        fontSize: '0.875rem',
        color: 'white',
        borderRadius: '5px',
        marginLeft: 8,
        minWidth: 120,
        "&:hover": {
            background: "#454343"
        },
        [theme.breakpoints.down('xs')]: {
            minWidth: 96,
            padding: '8px 14px',
            marginLeft: 0,
        }
    },
    placeBtn: {
        display: 'flex',
        justifyContent: 'end',
        padding: '20px 0',
        marginTop: 20,
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'center',
            flexWrap: 'nowrap',
            gap: theme.spacing(1),
            '& > *': {
                flex: '0 0 auto',
            },
        },
    },
    cardRoot: {
        border: '1px solid #D6D6D6',
        borderRadius: 5,
        minHeight: 240,
        backgroundColor: ' #F9F9F9',
        height: 'auto'
    },
    title: {
        fontSize: 15,
        fontWeight: 700,
        lineHeight: "18px",
        color: '#4D5766'
    },
    pos: {
        marginBottom: 12,
    },
    buttonWrap: {
        width: '100%',
        textAlign: 'center',
        margin: '10px auto'
    },
    removeBtn: {
        background: '#E23A3A',
        color: '#fff',
        height: 32,
        width: 32,
        borderRadius: '50%',
        '&:hover': {
            background: '#c20b0b',
            color: '#fff'
        }
    },
    noHeading: {
        fontWeight: 400,
        fontSize: '10px',
        lineHeight: '12px',
        marginTop: 15
    },
    noOfItemWrap: {
        display: "flex",
        width: "100%",
        marginTop: 20
    },
    inputStyle: {
        minWidth: 130,
        width: '100%',
        marginTop: 16,
        marginLeft: 0
    },
    itemContain: {
        height: 'auto'
    },
    cardGridItem: {
        height: 'auto'
    },
    recruitmentBanner: {
        marginTop: 8,
        marginBottom: 4,
        padding: '10px 16px',
        borderRadius: 6,
        backgroundColor: 'rgba(12, 41, 84, 0.06)',
        border: '1px solid rgba(12, 41, 84, 0.18)',
    },
    recruitmentBannerTitle: {
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#0C2954',
    },
    recruitmentBannerSubtitle: {
        fontSize: '0.8rem',
        marginTop: 2,
        color: '#4D5766',
    },
}));

const CreateProjectForm = ({
    handleInputChange,
    formState,
    customerList,
    customerAdminList,
    projectAdminList,
    project_id,
    handleCancelCreate,
    formValidationCheck,
    loading,
    handleBack,
    assignLicenseForm,
    handleChangeAsl,
    handleAddAssignLicence,
    handleRemoveAssignLicence,
    assessmentList,
    apprenticeshipList,
    courseList,
    learningAccess,
}) => {

    const classes = useStyles();
    const role = String(localStorage.getItem('role') || '').toLowerCase();
    const isSuperAdmin = role === USER_ROLES.SUPER_ADMIN;
    const canViewLearningAccess = isSuperAdmin
        || role === USER_ROLES.CUSTOMER_ADMIN
        || role === USER_ROLES.PROJECT_ADMIN;
    const showLearningAccess = !!project_id && canViewLearningAccess;
    const learningAccessValue = learningAccess || {
        plan: null,
        access_state: 'not_configured',
        access_active: false,
        access_days_remaining: 0,
    };

    const getAssessments = () => {
        return (
            assignLicenseForm.assessments.map((item, key) => (
                <div key={`assisment-${key}`} className={classes.itemContain}>
                    <Card className={classes.cardRoot} style={key > 0 ? { marginTop: 30 } : null}>
                        <CardContent>
                            <Typography className={classes.title} color="textSecondary" gutterBottom>
                                Assessments Allocated : { item?.user_count || 0 } / {item?.count || 0}
                            </Typography>
                            <OutlinedSelect
                                val={item.assessment_id}
                                name="assessment_id"
                                label="Select Assessments"
                                variant="outlined"
                                handleChange={(evt) => {
                                    let check = assignLicenseForm.assessments.find(cr => cr.assessment_id === evt.target.value && evt.target.value);
                                    if(check){
                                        notify('error', `This assessment is already selected!`);
                                        return null;
                                    }
                                    handleChangeAsl(evt, key, 'assessments')
                                }}
                                styleOverrides={{ marginTop: 36, marginBottom: 20, width: '100%' }}
                                options={assessmentList}
                                menuProps={{ style: { height: 400, zIndex: 9999 } }}
                            />
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Number of licenses"
                                value={item.count}
                                type="text"
                                className={classes.inputStyle}
                                name="count"
                                disabled={!item.assessment_id}
                                inputProps={{ max: item?.maxCount }}
                                onChange={evt => {
                                    if(evt.target.value && !IsNumeric(evt.target.value)){
                                        return null;
                                    }
                                    if(evt.target.value && evt.target.value > item?.maxCount){
                                        notify("error", `You can't assign more then ${item?.maxCount}`);
                                        return null;
                                    }
                                    handleChangeAsl(evt, key, "assessments");
                                }}
                            />
                        </CardContent>
                    </Card>
                    <div className={classes.buttonWrap} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                        {key === (assignLicenseForm.assessments.length - 1) && <AddMoreButton action={'assessments'} handleAddNew={handleAddAssignLicence} disabled={assignLicenseForm.assessments.length >= assessmentList.length} />}
                        {<RemoveButton index={key} action={'assessments'} handleClick={handleRemoveAssignLicence} />}
                    </div>
                </div>
            ))
        )
    }

    const getCourses = () => {
        return (assignLicenseForm.courses.map((item, key) => (
                <div key={`course-${key}`} className={classes.itemContain}>
                    <Card className={classes.cardRoot} style={key > 0 ? { marginTop: 30 } : null}>
                        <CardContent>
                            <Typography className={classes.title} color="textSecondary" gutterBottom>
                                Courses Allocated : { item?.user_count || 0 } / {item?.count || 0}
                            </Typography>
                            <OutlinedSelect
                                val={item.course_id}
                                name="course_id"
                                label="Select Courses"
                                variant="outlined"
                                handleChange={(evt) => {
                                    let check = assignLicenseForm.courses?.find(cr => cr?.course_id === evt.target.value && evt.target.value);
                                    if(check){
                                        notify('error', `This course is already selected!`);
                                        return null;
                                    }
                                    return handleChangeAsl(evt, key, 'courses');
                                }}
                                styleOverrides={{ marginTop: 36, marginBottom: 20, width: '100%' }}
                                options={courseList}
                                menuProps={{ style: { height: 400, zIndex: 9999 } }}
                            />
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Number of licenses"
                                value={item.count}
                                type="text"
                                className={classes.inputStyle}
                                name="count"
                                disabled={!item.course_id}
                                inputProps={{ max: item?.maxCount }}
                                onChange={evt => {
                                    if(evt.target.value && !IsNumeric(evt.target.value)){
                                        return null;
                                    }
                                    if(evt.target.value && evt.target.value > item?.maxCount){
                                        notify("error", `You can't assign more then ${item?.maxCount}`);
                                        return null;
                                    }
                                    handleChangeAsl(evt, key, "courses");
                                }}
                            />
                        </CardContent>
                    </Card>
                    <div className={classes.buttonWrap} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                        {key === (assignLicenseForm.courses.length - 1) && <AddMoreButton action={'courses'} handleAddNew={handleAddAssignLicence} disabled={assignLicenseForm.courses.length >= courseList.length} />}
                        {<RemoveButton index={key} action={'courses'} handleClick={handleRemoveAssignLicence} />}
                    </div>
                </div>
            ))
        )
    }

    const getApprenticeships = () => {
        return (
            assignLicenseForm.apprenticeships.map((item, key) => (
                <div key={`assisment-${key}`} className={classes.itemContain}>
                    <Card className={classes.cardRoot} style={key > 0 ? { marginTop: 30 } : null}>
                        <CardContent>
                            <Typography className={classes.title} color="textSecondary" gutterBottom>
                                Apprenticeships Allocated : { item?.user_count || 0 } / {item?.count || 0}
                            </Typography>
                            <OutlinedSelect
                                val={item.apprenticeship_id}
                                name="apprenticeship_id"
                                label="Select Apprenticeships"
                                variant="outlined"
                                handleChange={(evt) => {
                                    let check = assignLicenseForm.apprenticeships?.find(cr => cr?.apprenticeship_id === evt.target.value && evt.target.value);
                                    if(check){
                                        notify('error', `This apprenticeship is already selected!`);
                                        return null;
                                    }
                                    handleChangeAsl(evt, key, 'apprenticeships')
                                }}
                                styleOverrides={{ marginTop: 36, marginBottom: 20, width: '100%' }}
                                options={apprenticeshipList}
                                menuProps={{ style: { height: 400, zIndex: 9999 } }}
                            />
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Number of licenses"
                                value={item.count}
                                type="text"
                                className={classes.inputStyle}
                                name="count"
                                disabled={!item.apprenticeship_id}
                                inputProps={{ max: item?.maxCount }}
                                onChange={evt => {
                                    if(evt.target.value && !IsNumeric(evt.target.value)){
                                        return null;
                                    }
                                    if(evt.target.value && evt.target.value > item?.maxCount){
                                        notify("error", `You can't assign more then ${item?.maxCount}`);
                                        return null;
                                    }
                                    handleChangeAsl(evt, key, "apprenticeships");
                                }}
                            />
                        </CardContent>
                    </Card>
                    <div className={classes.buttonWrap} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                        {key === (assignLicenseForm.apprenticeships.length - 1) && <AddMoreButton action={'apprenticeships'} handleAddNew={handleAddAssignLicence} disabled={assignLicenseForm.apprenticeships.length >= apprenticeshipList.length} />}
                        {<RemoveButton index={key} action={'apprenticeships'} handleClick={handleRemoveAssignLicence} />}
                    </div>
                </div>
            ))
        )
    }

    const getLabs = () => {
        return (assignLicenseForm.labs.map((item, key) => (
                <div key={`labs-${key}`} className={classes.itemContain}>
                    <Card className={classes.cardRoot} style={key > 0 ? { marginTop: 75 } : null}>
                        <CardContent>
                            <Typography className={classes.title} color="textSecondary" gutterBottom>
                                Labs Allocated : { item?.user_count || 0 } / {item?.count || 0}
                            </Typography>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Select Labs"
                                value={item.name}
                                disabled
                                style={{ marginTop: 36 }}
                            />
                            <NumberInput
                                value={item?.count || 0}
                                index={key}
                                maxNum={null}
                                action="labs"
                                disabled={true}
                            />
                            {/* <OutlinedSelect
                                val={item.lab_id}
                                name="lab_id"
                                placeholder='Select Labs'
                                variant="standard"
                                handleChange={(evt) => handleChangeAsl(evt, key, 'labs')}
                                styleOverrides={{ marginTop: 36, marginBottom: 20 }}
                                options={labsList}
                                menuProps={{ style: { height: 400, zIndex: 9999 } }}
                                selectStyle={{ maxWidth: 226, minWidth: 226, padding: "4px 10px" }}
                            />
                            <div className={classes.noOfItemWrap}>
                                <Typography className={classes.noHeading} >Number of licenses</Typography>
                                <Input
                                    disableUnderline
                                    value={item.count}
                                    type="text"
                                    className={classes.inputStyle}
                                    placeholder={"Number"}
                                    name="count"
                                    onChange={evt => {
                                        if(evt.target.value && !IsNumeric(evt.target.value)){
                                            return null;
                                        }
                                        if(evt.target.value && evt.target.value > item?.maxCount){
                                            notify("error", `You can't assign more then ${item?.maxCount}`);
                                            return null;
                                        }
                                        handleChangeAsl(evt, key, "labs");
                                    }}
                                />
                            </div> */}
                        </CardContent>
                    </Card>
                    {/* <div className={classes.buttonWrap}>
                        {key === 0 && <AddMoreButton action={'labs'} handleAddNew={handleAddAssignLicence} />}
                        {key > 0 && <RemoveButton index={key} action={'labs'} handleClick={handleRemoveAssignLicence} />}
                    </div> */}
                </div>
            ))
        )
    }

    return (
        <React.Fragment>
            {!!project_id && !!formState.is_recruitment_project && (
                <Grid container style={{ marginTop: 8 }}>
                    <Grid item xs={12}>
                        <div className={classes.recruitmentBanner}>
                            <Typography className={classes.recruitmentBannerTitle}>
                                This is a recruitment project
                            </Typography>
                            <Typography className={classes.recruitmentBannerSubtitle}>
                                Enable Talent Access Email: {formState.enable_talent_access_email ? 'Enabled' : 'Disabled'}
                            </Typography>
                        </div>
                    </Grid>
                </Grid>
            )}
            <Grid container spacing={3} style={{ marginTop: 8 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Project Name"
                        value={formState.project_name}
                        name="project_name"
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <OutlinedSelect
                        val={formState.company_id}
                        name="company_id"
                        label="Company Name"
                        variant="outlined"
                        handleChange={handleInputChange}
                        styleOverrides={{ width: '100%', marginTop: 0 }}
                        options={customerList}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <OutlinedSelect
                        val={formState.company_admin_id}
                        name="company_admin_id"
                        label="Company Admin"
                        variant="outlined"
                        handleChange={handleInputChange}
                        styleOverrides={{ width: '100%', marginTop: 0 }}
                        options={customerAdminList}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <OutlinedSelect
                        val={formState.project_admin}
                        name="project_admin"
                        label="Project Admin"
                        variant="outlined"
                        handleChange={handleInputChange}
                        styleOverrides={{ width: '100%', marginTop: 0 }}
                        options={projectAdminList}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <OutlinedSelect
                        val={formState.status}
                        name="status"
                        label="Status"
                        variant="outlined"
                        disabled={project_id ? false : true}
                        handleChange={handleInputChange}
                        styleOverrides={{ width: '100%', marginTop: 0 }}
                        options={["Open", "Complete"]}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Description"
                        value={formState.description}
                        name="description"
                        disabled={!!project_id}
                        onChange={handleInputChange}
                        multiline
                        rows={4}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    {project_id && formState.is_recruitment_project ? (
                        // For recruitment projects, show read-only info in the banner above instead of a toggle
                        null
                    ) : (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!formState.enable_talent_access_email}
                                    onChange={handleInputChange}
                                    color="primary"
                                    name="enable_talent_access_email"
                                />
                            }
                            label="Enable Talent Access Email"
                            labelPlacement="end"
                        />
                    )}
                </Grid>
            </Grid>
            {showLearningAccess && (
                <div id={`learning-hub-${project_id}`}>
                    <LearningAccessSection
                        projectId={project_id}
                        learningAccess={learningAccessValue}
                        readOnly={!isSuperAdmin}
                    />
                </div>
            )}
            <Grid container spacing={4} style={{marginTop: 20}}>
                <Grid item xs={12}>
                    <Typography className={classes.title} color="textSecondary" gutterBottom>Assign licenses</Typography>
                </Grid>
            </Grid>
            <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={3} className={classes.cardGridItem}>
                    {getAssessments()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} className={classes.cardGridItem}>
                    {getCourses()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} className={classes.cardGridItem}>
                    {getApprenticeships()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} className={classes.cardGridItem}>
                    {getLabs()}
                </Grid>
            </Grid>
            <div className={classes.placeBtn}>
                <Button onClick={handleCancelCreate} color="primary" className={classes.cancelBtn}>
                    Cancel
                </Button>
                <Button onClick={handleBack} color="primary" className={classes.cancelBtn}>
                    Back
                </Button>
                <Button
                    color="primary"
                    variant="outlined"
                    className={classes.btn}
                    disabled={
                        !formState.project_name ||
                        !formState.company_id ||
                        !formState.company_admin_id
                    }
                    onClick={formValidationCheck}>
                    {loading ? <CircularProgress size={15} color="secondary" /> : 'Next'}
                </Button>
            </div>
        </React.Fragment>
    );
}
CreateProjectForm.propTypes = {
    handleInputChange: PropTypes.func.isRequired,
    formState: PropTypes.object.isRequired
}
export default CreateProjectForm;